(() => {
  if (window.inknfiberProductPageInitialized) return;
  window.inknfiberProductPageInitialized = true;

  const initializedPickers = new WeakSet();
  const quantityState = new WeakMap();
  const RETAIL_QUANTITY_MAX = 99;
  const B2B_QUANTITY_MAX = 250;

  const sizeAliases = {
    XS: ['XS', 'X-SMALL', 'EXTRA SMALL', 'EXTRA-SMALL'],
    S: ['S', 'SMALL'],
    M: ['M', 'MEDIUM'],
    L: ['L', 'LARGE'],
    XL: ['XL', 'X-LARGE', 'EXTRA LARGE', 'EXTRA-LARGE'],
    '2XL': ['2XL', '2X', '2X LARGE', '2X-LARGE', 'XXL', 'XX-LARGE'],
    '3XL': ['3XL', '3X', '3X LARGE', '3X-LARGE', 'XXXL', 'XXX-LARGE'],
    '4XL': ['4XL', '4X', '4X LARGE', '4X-LARGE', 'XXXXL', 'XXXX-LARGE'],
    '5XL': ['5XL', '5X', '5X LARGE', '5X-LARGE', 'XXXXXL', 'XXXXX-LARGE'],
  };

  const normalize = (value) => String(value || '').trim().toUpperCase();
  const compact = (value) => normalize(value).replace(/[^A-Z0-9]/g, '');
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const sizeKey = (value) => compact(value);
  const hasB2BCatalogQuery = () => new URLSearchParams(window.location.search).get('b2b_catalog') === '1';

  function updateB2BQuantityTiersVisibility() {
    document.querySelectorAll('[data-inknfiber-b2b-product-page-tiers]').forEach((tiers) => {
      tiers.toggleAttribute('hidden', !hasB2BCatalogQuery());
    });
  }

  function quantityMaxForMatrix(matrix) {
    const retailMax = Number.parseInt(matrix?.dataset.retailQuantityMax || '', 10) || RETAIL_QUANTITY_MAX;
    const b2bMax = Number.parseInt(matrix?.dataset.b2bQuantityMax || '', 10) || B2B_QUANTITY_MAX;
    const isEligibleB2BContext = matrix?.dataset.inknfiberB2bQuantityEligible === 'true';

    return isEligibleB2BContext ? b2bMax : retailMax;
  }

  function optionMatchesSize(value, size) {
    const normalized = normalize(value);
    const compacted = compact(value);
    const aliases = sizeAliases[normalize(size)] || [size];

    return aliases.some((alias) => normalized === normalize(alias) || compacted === compact(alias));
  }

  function optionsMatch(variantValue, selectedValue, sizeValue, isSizeOption) {
    if (!selectedValue) return true;
    if (isSizeOption) return optionMatchesSize(variantValue, sizeValue);
    return compact(variantValue) === compact(selectedValue);
  }

  function formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
    }).format((Number(cents) || 0) / 100);
  }

  function isB2BMatrixContext(matrix) {
    return matrix?.dataset.inknfiberB2bQuantityEligible === 'true';
  }

  function nativePriceBreaks(variant) {
    if (!Array.isArray(variant?.quantity_price_breaks)) return [];

    return variant.quantity_price_breaks
      .map((priceBreak) => ({
        minimumQuantity: Number.parseInt(String(priceBreak?.minimum_quantity || 0), 10),
        price: Number(priceBreak?.price),
      }))
      .filter((priceBreak) => priceBreak.minimumQuantity > 0 && Number.isFinite(priceBreak.price))
      .sort((left, right) => left.minimumQuantity - right.minimumQuantity);
  }

  function maximumForVariant(variant, matrix) {
    const matrixMaximum = quantityMaxForMatrix(matrix);
    const bulkOrderApi = window.InkNfiberB2BBulkOrder;

    if (isB2BMatrixContext(matrix) && bulkOrderApi?.getVariantConstraints) {
      return bulkOrderApi.getVariantConstraints(
        variant,
        bulkOrderApi.cartQuantityFor?.(variant?.id) || 0,
        matrixMaximum
      ).remaining;
    }

    const nativeMaximum = Number.parseInt(String(variant?.quantity_rule?.max || ''), 10);

    return Number.isFinite(nativeMaximum) && nativeMaximum > 0
      ? Math.min(matrixMaximum, nativeMaximum)
      : matrixMaximum;
  }

  function discountPercent(basePrice, unitPrice) {
    if (!(basePrice > 0) || !(unitPrice < basePrice)) return 0;
    return Math.max(0, Math.round(((basePrice - unitPrice) / basePrice) * 100));
  }

  function nativePricingForVariant(variant, quantity, matrix) {
    const basePrice = Math.max(0, Number(variant?.price) || 0);
    const minimum = Math.max(1, Number.parseInt(String(variant?.quantity_rule?.min || 1), 10) || 1);
    const maximum = maximumForVariant(variant, matrix);
    const bulkOrderApi = window.InkNfiberB2BBulkOrder;
    const constraints = isB2BMatrixContext(matrix) && bulkOrderApi?.getVariantConstraints
      ? bulkOrderApi.getVariantConstraints(
        variant,
        bulkOrderApi.cartQuantityFor?.(variant?.id) || 0,
        quantityMaxForMatrix(matrix)
      )
      : null;
    const existingCartQuantity = constraints?.cartQuantity || 0;
    const maximumTotal = constraints?.maximumTotal ?? maximum;
    const priceBreaks = nativePriceBreaks(variant).filter((priceBreak) => priceBreak.minimumQuantity <= maximumTotal);
    const tiers = [{ minimumQuantity: minimum, price: basePrice }];

    priceBreaks.forEach((priceBreak) => {
      if (priceBreak.minimumQuantity <= minimum) {
        tiers[0].price = priceBreak.price;
        return;
      }

      tiers.push(priceBreak);
    });

    const normalizedQuantity = normalizeQuantityValue(quantity, maximum);
    const tierQuantity = Math.max(existingCartQuantity + normalizedQuantity, minimum);
    let activeIndex = 0;

    tiers.forEach((tier, index) => {
      if (tierQuantity >= tier.minimumQuantity) activeIndex = index;
    });

    const normalizedTiers = tiers.map((tier, index) => ({
      ...tier,
      maximumQuantity: tiers[index + 1]?.minimumQuantity
        ? tiers[index + 1].minimumQuantity - 1
        : maximumTotal,
      discountPercent: discountPercent(basePrice, tier.price),
    }));
    const activeTier = normalizedTiers[activeIndex];

    return {
      activeIndex,
      activeTier,
      basePrice,
      hasPriceBreaks: priceBreaks.length > 0,
      lineTotal: normalizedQuantity * activeTier.price,
      maximum,
      maximumTotal,
      minimum,
      quantity: normalizedQuantity,
      tierQuantity,
      tiers: normalizedTiers,
      unitPrice: activeTier.price,
    };
  }

  function tierRangeLabel(tier) {
    if (!tier) return '';
    if (tier.maximumQuantity >= tier.minimumQuantity) {
      return `${tier.minimumQuantity}-${tier.maximumQuantity}`;
    }
    return `${tier.minimumQuantity}+`;
  }

  function createTierTableCell(text) {
    const cell = document.createElement('td');
    cell.textContent = text;
    return cell;
  }

  function renderB2BTierRows(tierBody, pricing) {
    const fragment = document.createDocumentFragment();
    const effectiveQuantity = Math.max(pricing.tierQuantity, pricing.minimum);

    pricing.tiers.forEach((tier, index) => {
      const row = document.createElement('tr');
      const quantityCell = createTierTableCell(tierRangeLabel(tier));
      const nextTier = pricing.tiers[index + 1];

      if (index === pricing.activeIndex) {
        row.classList.add('is-active');

        if (nextTier) {
          const needed = Math.max(0, nextTier.minimumQuantity - effectiveQuantity);

          if (needed > 0) {
            const note = document.createElement('span');
            note.className = 'inknfiber-b2b-tiers__tier-note inknfiber-dtf-size__tier-note';
            note.textContent = nextTier.discountPercent > 0
              ? `Add ${needed} more of this size to reach ${nextTier.discountPercent}% off savings.`
              : `Add ${needed} more of this size to reach the next price tier.`;
            quantityCell.appendChild(note);
          }
        }
      }

      row.append(
        quantityCell,
        createTierTableCell(`${formatMoney(tier.price)} each`),
        createTierTableCell(tier.discountPercent > 0 ? `${tier.discountPercent}% off` : '-'),
      );
      fragment.appendChild(row);
    });

    tierBody.replaceChildren(fragment);
  }

  function parseVariants(picker) {
    const script = picker.querySelector('[data-inknfiber-product-variants]');
    if (!script) return [];

    try {
      return JSON.parse(script.textContent || '[]');
    } catch (_) {
      return [];
    }
  }

  function getSelections(picker) {
    const selections = [];

    picker.querySelectorAll('.variant-picker__form fieldset[data-fieldset-index]').forEach((fieldset) => {
      const index = Number(fieldset.dataset.fieldsetIndex);
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      selections[index] = checked?.value || '';
    });

    picker.querySelectorAll('.variant-picker__form select').forEach((select, index) => {
      selections[index] = select.value || selections[index] || '';
    });

    return selections;
  }

  function findVariantForSize(variants, selections, sizeIndex, size) {
    return variants.find((variant) => {
      const options = [variant.option1, variant.option2, variant.option3];

      return selections.every((selection, index) => {
        const selectedValue = index === sizeIndex ? size : selection;
        return optionsMatch(options[index], selectedValue, size, index === sizeIndex);
      });
    }) || null;
  }

  function isInventoryTracked(variant) {
    return Boolean(variant?.inventory_management);
  }

  function isVariantAvailable(variant) {
    if (!variant) return false;
    if (variant.available === true) return true;
    return !isInventoryTracked(variant);
  }

  function selectedRows(matrix) {
    const quantityMax = quantityMaxForMatrix(matrix);

    return Array.from(matrix.querySelectorAll('[data-inknfiber-size-row]'))
      .map((row) => {
        const input = row.querySelector('[data-inknfiber-size-quantity]');
        return {
          row,
          size: row.dataset.size || '',
          quantity: normalizeQuantityValue(input?.value, quantityMax),
          variantId: row.dataset.variantId || '',
          available: row.dataset.available === 'true',
        };
      })
      .filter((item) => item.quantity > 0 && item.available);
  }

  function totalQuantity(matrix) {
    return selectedRows(matrix).reduce((sum, item) => sum + item.quantity, 0);
  }

  function sizeBreakdown(matrix) {
    return selectedRows(matrix).map((item) => `${item.size}-${item.quantity}`).join(', ');
  }

  function getQuantityMap(picker) {
    let quantities = quantityState.get(picker);

    if (!quantities) {
      quantities = new Map();
      quantityState.set(picker, quantities);
    }

    return quantities;
  }

  function getStoredQuantity(picker, size, fallback = 0) {
    const quantities = getQuantityMap(picker);
    const key = sizeKey(size);

    return quantities.has(key) ? quantities.get(key) : fallback;
  }

  function setStoredQuantity(picker, size, quantity) {
    const matrix = picker.querySelector('[data-inknfiber-size-matrix]');
    getQuantityMap(picker).set(sizeKey(size), normalizeQuantityValue(quantity, quantityMaxForMatrix(matrix)));
  }

  function normalizeQuantityValue(value, maximum = RETAIL_QUANTITY_MAX) {
    const parsed = Number.parseInt(String(value || '0'), 10);
    return clamp(Number.isFinite(parsed) ? parsed : 0, 0, maximum);
  }

  function updateQuantityInput(picker, input, quantity) {
    if (!(input instanceof HTMLInputElement)) return;

    const row = input.closest('[data-inknfiber-size-row]');
    const matrix = row?.closest('[data-inknfiber-size-matrix]');
    if (!row || !matrix || !picker.contains(matrix)) return;

    const normalizedQuantity = normalizeQuantityValue(quantity, quantityMaxForMatrix(matrix));
    input.value = String(normalizedQuantity);
    matrix.dataset.inknfiberActiveSize = row.dataset.size || '';
    setStoredQuantity(picker, row.dataset.size || '', normalizedQuantity);
    updateMatrix(picker);
  }

  function captureMatrixQuantities(picker) {
    const matrix = picker.querySelector('[data-inknfiber-size-matrix]');
    if (!matrix) return;

    matrix.querySelectorAll('[data-inknfiber-size-row]').forEach((row) => {
      const input = row.querySelector('[data-inknfiber-size-quantity]');
      setStoredQuantity(picker, row.dataset.size || '', input?.value || 0);
    });
  }

  function collectProperties(form) {
    const properties = {};
    const formData = new FormData(form);

    for (const [name, value] of formData.entries()) {
      const match = String(name).match(/^properties\[(.+)]$/);
      if (!match) continue;
      const cleanValue = String(value || '').trim();
      if (cleanValue) properties[match[1]] = cleanValue;
    }

    return properties;
  }

  function getProductContextRoot(picker) {
    return picker.closest('.product-details')
      || picker.closest('#quick-add-modal-content')
      || picker.closest('.quick-add-modal__content')
      || picker.parentElement;
  }

  function getProductForm(picker) {
    const root = getProductContextRoot(picker);
    return root?.querySelector('product-form-component form') || null;
  }

  function getB2BTierDisplay(picker) {
    return getProductContextRoot(picker)?.querySelector('[data-inknfiber-b2b-tier-display]') || null;
  }

  function updateB2BTierDisplay(picker, matrix, variants, combinedSubtotal, selectedSizeCount) {
    const display = getB2BTierDisplay(picker);
    if (!display || !isB2BMatrixContext(matrix)) return;

    const rows = Array.from(matrix.querySelectorAll('[data-inknfiber-size-row]'));
    let activeRow = rows.find((row) => row.dataset.size === matrix.dataset.inknfiberActiveSize);

    if (!activeRow || activeRow.dataset.available !== 'true') {
      activeRow = rows.find((row) => Number(row.querySelector('[data-inknfiber-size-quantity]')?.value || 0) > 0)
        || rows.find((row) => row.dataset.available === 'true')
        || null;
    }

    if (!activeRow) return;

    matrix.dataset.inknfiberActiveSize = activeRow.dataset.size || '';
    const variant = variants.find((candidate) => String(candidate.id) === String(activeRow.dataset.variantId));
    if (!variant) return;

    const quantity = Number(activeRow.querySelector('[data-inknfiber-size-quantity]')?.value || 0);
    const pricing = nativePricingForVariant(variant, quantity, matrix);
    const tierTable = display.querySelector('[data-inknfiber-b2b-tier-table]');
    const tierBody = display.querySelector('[data-inknfiber-b2b-tier-body]');
    const emptyState = display.querySelector('[data-inknfiber-b2b-tier-empty]');
    const activeTierLabel = display.querySelector('[data-inknfiber-b2b-active-tier]');
    const selectedTotal = display.querySelector('[data-inknfiber-b2b-selected-total]');
    const combinedTotal = display.querySelector('[data-inknfiber-b2b-combined-total]');

    if (tierTable) tierTable.hidden = !pricing.hasPriceBreaks;
    if (emptyState) emptyState.hidden = pricing.hasPriceBreaks;
    if (tierBody && pricing.hasPriceBreaks) renderB2BTierRows(tierBody, pricing);

    if (activeTierLabel) {
      activeTierLabel.textContent = pricing.hasPriceBreaks
        ? `Active tier for ${activeRow.dataset.size}: ${tierRangeLabel(pricing.activeTier)}. ${formatMoney(pricing.unitPrice)} each.`
        : `Native B2B unit price for ${activeRow.dataset.size}: ${formatMoney(pricing.unitPrice)}.`;
    }

    if (selectedTotal) {
      selectedTotal.hidden = pricing.quantity <= 0;
      selectedTotal.textContent = pricing.quantity > 0
        ? `${activeRow.dataset.size} total: ${formatMoney(pricing.lineTotal)}`
        : '';
    }

    if (combinedTotal) {
      combinedTotal.hidden = selectedSizeCount <= 1;
      combinedTotal.textContent = selectedSizeCount > 1
        ? `Selected product subtotal: ${formatMoney(combinedSubtotal)}`
        : '';
    }
  }

  function getStockStatus(picker) {
    const root = getProductContextRoot(picker);
    return root?.querySelector('[data-inknfiber-stock-status]') || null;
  }

  function setStockStatusVisible(picker, isOutOfStock) {
    const status = getStockStatus(picker);
    if (!status) return;

    status.classList.toggle('hidden', !isOutOfStock);
  }

  function findVariantForSelections(variants, selections) {
    return variants.find((variant) => {
      const options = [variant.option1, variant.option2, variant.option3];
      return selections.every((selection, index) => optionsMatch(options[index], selection, selection, false));
    }) || null;
  }

  function setStatus(matrix, message, type = '') {
    const status = matrix.querySelector('[data-inknfiber-size-status]');
    if (!status) return;

    status.textContent = message || '';
    status.dataset.status = type;
  }

  function setAddButtonState(picker, hasAvailableSize = true, hasSelectedQuantity = false) {
    const matrix = picker.querySelector('[data-inknfiber-size-matrix]');
    const form = getProductForm(picker);
    const button = form?.querySelector('[name="add"]');
    const acceleratedCheckout = form
      ?.closest('product-form-component')
      ?.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
    if (!matrix || !button) return;

    if (!hasAvailableSize) {
      button.disabled = true;
      acceleratedCheckout?.setAttribute('hidden', 'true');
      return;
    }

    button.disabled = false;
    syncAcceleratedCheckoutState(acceleratedCheckout, hasSelectedQuantity);
  }

  function syncAcceleratedCheckoutState(acceleratedCheckout, isEnabled = false) {
    if (!acceleratedCheckout) return;

    const restoreCheckout = () => {
      acceleratedCheckout.removeAttribute('hidden');
      acceleratedCheckout.classList.toggle('is-inknfiber-matrix-disabled', !isEnabled);
      acceleratedCheckout.setAttribute('aria-disabled', String(!isEnabled));
      acceleratedCheckout.dataset.inknfiberMatrixBuyNow = 'true';

      acceleratedCheckout.querySelectorAll('button, input').forEach((control) => {
        if (control instanceof HTMLButtonElement || control instanceof HTMLInputElement) {
          control.disabled = !isEnabled;
        }
        control.toggleAttribute('disabled', !isEnabled);
        control.setAttribute('aria-disabled', String(!isEnabled));
      });
    };

    restoreCheckout();
    requestAnimationFrame(restoreCheckout);
    window.setTimeout(restoreCheckout, 50);
  }

  function updateMatrix(picker) {
    const matrix = picker.querySelector('[data-inknfiber-size-matrix]');
    if (!matrix) return;

    const variants = parseVariants(picker);
    const sizeIndex = Number(matrix.dataset.sizeOptionPosition || 1) - 1;
    const selections = getSelections(picker);
    const isDynamicB2B = isB2BMatrixContext(matrix) && Boolean(getB2BTierDisplay(picker));
    let total = 0;
    let combinedSubtotal = 0;
    let selectedSizeCount = 0;
    let hasUnavailableSelection = false;
    let hasAvailableSize = false;

    matrix.querySelectorAll('[data-inknfiber-size-row]').forEach((row) => {
      const size = row.dataset.size || '';
      const input = row.querySelector('[data-inknfiber-size-quantity]');
      const quantityControls = row.querySelectorAll('[data-inknfiber-size-minus], [data-inknfiber-size-plus], [data-inknfiber-size-quantity]');
      const price = row.querySelector('[data-inknfiber-size-price]');
      const stock = row.querySelector('[data-inknfiber-size-stock]');
      const lineTotal = row.querySelector('[data-inknfiber-size-line-total]');
      const variant = findVariantForSize(variants, selections, sizeIndex, size);
      const bulkOrderApi = window.InkNfiberB2BBulkOrder;
      const b2bConstraints = isB2BMatrixContext(matrix) && bulkOrderApi?.getVariantConstraints
        ? bulkOrderApi.getVariantConstraints(
          variant,
          bulkOrderApi.cartQuantityFor?.(variant?.id) || 0,
          quantityMaxForMatrix(matrix)
        )
        : null;
      const isAvailable = b2bConstraints ? b2bConstraints.available : isVariantAvailable(variant);
      const variantMaximum = maximumForVariant(variant, matrix);
      const fallbackQuantity = normalizeQuantityValue(input?.value, variantMaximum);
      const storedQuantity = getStoredQuantity(picker, size, fallbackQuantity);
      const quantity = isAvailable
        ? b2bConstraints && bulkOrderApi?.normalizeAddition
          ? bulkOrderApi.normalizeAddition(storedQuantity, b2bConstraints)
          : normalizeQuantityValue(storedQuantity, variantMaximum)
        : 0;
      const nativePricing = nativePricingForVariant(variant, quantity, matrix);

      if (input && Number(input.value || 0) !== quantity) {
        input.value = String(quantity);
      }
      if (input) {
        input.max = String(variantMaximum);
        if (b2bConstraints) input.step = String(b2bConstraints.increment);
      }

      total += quantity;
      if (quantity > 0) {
        selectedSizeCount += 1;
        combinedSubtotal += nativePricing.lineTotal;
      }
      row.dataset.variantId = variant?.id ? String(variant.id) : '';
      row.dataset.available = isAvailable ? 'true' : 'false';
      row.classList.toggle('is-unavailable', !isAvailable);
      row.classList.toggle('is-selected', quantity > 0);

      quantityControls.forEach((control) => {
        if (control instanceof HTMLButtonElement || control instanceof HTMLInputElement) {
          control.disabled = !isAvailable;
        }
        control.toggleAttribute('disabled', !isAvailable);
        control.setAttribute('aria-disabled', String(!isAvailable));
      });

      const minusButton = row.querySelector('[data-inknfiber-size-minus]');
      const plusButton = row.querySelector('[data-inknfiber-size-plus]');
      if (minusButton) {
        minusButton.disabled = !isAvailable || quantity <= 0;
        minusButton.toggleAttribute('disabled', minusButton.disabled);
        minusButton.setAttribute('aria-disabled', String(minusButton.disabled));
      }
      if (plusButton) {
        plusButton.disabled = !isAvailable || quantity >= variantMaximum;
        plusButton.toggleAttribute('disabled', plusButton.disabled);
        plusButton.setAttribute('aria-disabled', String(plusButton.disabled));
      }

      if (isAvailable) {
        hasAvailableSize = true;
      }

      if (price) {
        const previousPrice = price.textContent.trim();
        const nextPrice = variant
          ? `${formatMoney(isDynamicB2B ? nativePricing.unitPrice : variant.price)}${isDynamicB2B ? ' each' : ''}`
          : previousPrice || 'Unavailable';
        price.textContent = nextPrice;
        price.dataset.lastValue = nextPrice;
      }
      if (lineTotal) {
        lineTotal.hidden = !isDynamicB2B || quantity <= 0;
        lineTotal.textContent = isDynamicB2B && quantity > 0
          ? `Total ${formatMoney(nativePricing.lineTotal)}`
          : '';
      }
      if (stock) {
        if (!variant) {
          stock.textContent = stock.textContent.trim() || 'Not offered';
          stock.dataset.status = 'out';
        } else if (!isAvailable) {
          stock.textContent = 'Out of stock';
          stock.dataset.status = 'out';
        } else if (isInventoryTracked(variant) && Number.isFinite(Number(variant.inventory_quantity))) {
          stock.textContent = `${Math.max(Number(variant.inventory_quantity), 0)} in stock`;
          stock.dataset.status = 'in';
        } else {
          stock.textContent = 'In stock';
          stock.dataset.status = 'in';
        }
      }

      if (quantity > 0 && !isAvailable) {
        hasUnavailableSelection = true;
      }

      setStoredQuantity(picker, size, quantity);
    });

    const totalLabel = matrix.querySelector('[data-inknfiber-size-total]');
    if (totalLabel) {
      totalLabel.textContent = total === 1 ? '1 selected' : `${total} selected`;
    }

    if (hasUnavailableSelection) {
      setStatus(matrix, 'Some selected sizes are unavailable for the current option selection.', 'error');
    } else if (total > 0) {
      setStatus(matrix, 'Ready to add to cart.', 'success');
    } else {
      setStatus(matrix, '');
    }

    setAddButtonState(picker, hasAvailableSize, total > 0);
    setStockStatusVisible(picker, !hasAvailableSize);
    updateB2BTierDisplay(picker, matrix, variants, combinedSubtotal, selectedSizeCount);
  }

  function updateStockStatus(picker) {
    const matrix = picker.querySelector('[data-inknfiber-size-matrix]');

    if (matrix) {
      updateMatrix(picker);
      return;
    }

    const variants = parseVariants(picker);
    const variant = findVariantForSelections(variants, getSelections(picker));
    setStockStatusVisible(picker, !isVariantAvailable(variant));
  }

  function updateCartBubble(cart) {
    if (window.InkNfiberCart?.dispatchCartUpdate) {
      window.InkNfiberCart.dispatchCartUpdate(cart, { source: 'inknfiber-product-size-matrix' });
    }
  }

  function showFormMessage(form, message, type = 'error') {
    const error = form.querySelector('.product-form-text__error');
    if (!error) return;

    error.classList.remove('hidden');
    error.dataset.status = type;
    error.textContent = message;

    if (type === 'success') {
      window.setTimeout(() => error.classList.add('hidden'), 6000);
    }
  }

  function buildMatrixCartItems(form, matrix) {
    const properties = collectProperties(form);
    const breakdown = sizeBreakdown(matrix);

    return selectedRows(matrix).map((row) => ({
      id: Number(row.variantId),
      quantity: row.quantity,
      properties: {
        ...properties,
        Size: row.size,
        'Size breakdown': breakdown,
      },
    }));
  }

  async function addItemsToCart(items, options = {}) {
    if (window.InkNfiberCart?.addItems) {
      return window.InkNfiberCart.addItems(items, options);
    }

    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({ items }),
    });
    const body = await response.json();

    if (!response.ok) {
      throw new Error(body?.description || body?.message || options.errorMessage || 'Unable to add the selected sizes to cart.');
    }

    if (options.refresh === false) {
      return { body, cart: null };
    }

    const cartResponse = await fetch(`${root}cart.js`);
    const cart = await cartResponse.json();
    updateCartBubble(cart);
    return { body, cart };
  }

  function checkoutUrl() {
    return `${window.Shopify?.routes?.root || '/'}checkout`;
  }

  function matrixContextFromElement(element) {
    const details = element.closest('.product-details')
      || element.closest('#quick-add-modal-content')
      || element.closest('.quick-add-modal__content');
    const picker = details?.querySelector('variant-picker');
    const matrix = picker?.querySelector('[data-inknfiber-size-matrix]');
    const form = details?.querySelector('product-form-component form');

    if (!details || !picker || !matrix || !form) return null;

    return { details, picker, matrix, form };
  }

  function setMatrixRowError(row, message) {
    const error = row?.querySelector('[data-inknfiber-size-error]');
    const input = row?.querySelector('[data-inknfiber-size-quantity]');
    if (!error) return;

    error.textContent = message || '';
    error.hidden = !message;
    input?.setAttribute('aria-invalid', String(Boolean(message)));
  }

  async function refreshB2BCartLimits(picker, matrix, form) {
    if (!isB2BMatrixContext(matrix)) return true;

    const bulkOrderApi = window.InkNfiberB2BBulkOrder;
    if (!bulkOrderApi?.refreshCart) {
      const message = 'B2B cart limits are still loading. Refresh the page and try again.';
      setStatus(matrix, message, 'error');
      showFormMessage(form, message, 'error');
      return false;
    }

    try {
      await bulkOrderApi.refreshCart(window, true);
      updateStockStatus(picker);
      return true;
    } catch (error) {
      const message = error.message || 'Unable to verify current cart quantities.';
      setStatus(matrix, message, 'error');
      showFormMessage(form, message, 'error');
      return false;
    }
  }

  function validateMatrixSelection(picker, matrix, form) {
    if (isB2BMatrixContext(matrix) && window.InkNfiberB2BBulkOrder?.validateAddition) {
      const variants = parseVariants(picker);
      const messages = [];

      matrix.querySelectorAll('[data-inknfiber-size-row]').forEach((row) => {
        const input = row.querySelector('[data-inknfiber-size-quantity]');
        const variant = variants.find((candidate) => String(candidate.id) === String(row.dataset.variantId || ''));
        const rawValue = input?.value ?? '0';
        let message = '';

        if (!variant && Number(rawValue) > 0) {
          message = 'This size cannot be matched to an available variant.';
        } else if (variant) {
          const constraints = window.InkNfiberB2BBulkOrder.getVariantConstraints(
            variant,
            window.InkNfiberB2BBulkOrder.cartQuantityFor?.(variant.id) || 0,
            quantityMaxForMatrix(matrix)
          );
          const validation = window.InkNfiberB2BBulkOrder.validateAddition(rawValue, constraints);
          if (!validation.valid) message = validation.message;
        }

        setMatrixRowError(row, message);
        if (message) messages.push(`${row.dataset.size || 'Size'}: ${message}`);
      });

      if (messages.length) {
        const message = `Review the selected quantities. ${messages.join(' ')}`;
        setStatus(matrix, message, 'error');
        showFormMessage(form, message, 'error');
        return null;
      }
    }

    const rows = selectedRows(matrix);

    if (!rows.length) {
      const message = 'Please select at least one size.';
      setStatus(matrix, message, 'error');
      showFormMessage(form, message, 'error');
      return null;
    }

    const unavailable = rows.filter((row) => !row.variantId || !row.available);
    if (unavailable.length) {
      const message = 'Some selected sizes could not be matched to available product variants.';
      setStatus(matrix, message, 'error');
      showFormMessage(form, message, 'error');
      return null;
    }

    return rows;
  }

  async function addMatrixToCart(event, picker, matrix, form) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (matrix.dataset.adding === 'true') return;

    const button = form.querySelector('[name="add"]');
    const originalMarkup = button?.innerHTML;
    let preserveValidationMessage = false;

    if (button) {
      button.disabled = true;
      button.textContent = 'Checking cart...';
    }
    matrix.dataset.adding = 'true';

    try {
      if (!await refreshB2BCartLimits(picker, matrix, form)) {
        preserveValidationMessage = true;
        return;
      }

      const rows = validateMatrixSelection(picker, matrix, form);
      if (!rows) {
        preserveValidationMessage = true;
        return;
      }

      const items = buildMatrixCartItems(form, matrix);
      if (button) button.textContent = 'Adding...';
      await addItemsToCart(items, {
        source: 'inknfiber-product-size-matrix',
        errorMessage: 'Unable to add the selected sizes to cart.',
      });
      setStatus(matrix, `${rows.reduce((sum, row) => sum + row.quantity, 0)} items added to cart.`, 'success');
      showFormMessage(form, 'Selected sizes were added to cart.', 'success');
    } catch (error) {
      const message = error.message || 'Unable to add the selected sizes to cart.';
      console.error('[InkNfiber product size matrix] Add to cart failed:', error);
      setStatus(matrix, message, 'error');
      showFormMessage(form, message, 'error');
    } finally {
      delete matrix.dataset.adding;
      if (button) {
        button.disabled = false;
        if (originalMarkup) button.innerHTML = originalMarkup;
      }
      if (!preserveValidationMessage) updateMatrix(picker);
    }
  }

  async function buyMatrixNow(event, picker, matrix, form, acceleratedCheckout) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (matrix.dataset.adding === 'true') return;

    const checkoutControls = acceleratedCheckout.querySelectorAll('button, input');
    let preserveValidationMessage = false;
    matrix.dataset.adding = 'true';
    acceleratedCheckout.classList.add('is-inknfiber-matrix-loading');
    checkoutControls.forEach((control) => {
      if (control instanceof HTMLButtonElement || control instanceof HTMLInputElement) {
        control.disabled = true;
      }
      control.setAttribute('aria-disabled', 'true');
    });
    setStatus(matrix, 'Preparing checkout...', 'success');

    try {
      if (!await refreshB2BCartLimits(picker, matrix, form)) {
        preserveValidationMessage = true;
        return;
      }

      const rows = validateMatrixSelection(picker, matrix, form);
      if (!rows) {
        preserveValidationMessage = true;
        return;
      }

      const items = buildMatrixCartItems(form, matrix);
      await addItemsToCart(items, {
        source: 'inknfiber-product-size-matrix-buy-now',
        errorMessage: 'Unable to start checkout with the selected sizes.',
        refresh: false,
      });
      window.location.href = checkoutUrl();
    } catch (error) {
      const message = error.message || 'Unable to start checkout with the selected sizes.';
      console.error('[InkNfiber product size matrix] Buy it now failed:', error);
      setStatus(matrix, message, 'error');
      showFormMessage(form, message, 'error');
      preserveValidationMessage = true;
    } finally {
      delete matrix.dataset.adding;
      acceleratedCheckout.classList.remove('is-inknfiber-matrix-loading');
      checkoutControls.forEach((control) => {
        if (control instanceof HTMLButtonElement || control instanceof HTMLInputElement) {
          control.disabled = false;
        }
        control.removeAttribute('aria-disabled');
      });
      if (!preserveValidationMessage) updateMatrix(picker);
    }
  }

  function initPicker(picker) {
    if (initializedPickers.has(picker)) return;
    initializedPickers.add(picker);

    const matrix = picker.querySelector('[data-inknfiber-size-matrix]');

    if (matrix) {
      const form = getProductForm(picker);
      const productForm = form?.closest('product-form-component');
      const quantityMax = quantityMaxForMatrix(matrix);

      form?.setAttribute('data-inknfiber-size-matrix-form', 'true');
      productForm?.setAttribute('data-inknfiber-size-matrix-form', 'true');
      form?.querySelector('[name="add"]')?.setAttribute('data-inknfiber-matrix-add-to-cart', 'true');
      matrix.dataset.quantityMax = String(quantityMax);
      matrix.classList.toggle('is-b2b-quantity-context', quantityMax === B2B_QUANTITY_MAX);
      matrix.querySelectorAll('[data-inknfiber-size-quantity]').forEach((input) => {
        input.max = String(quantityMax);
      });

      picker.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)) return;
        const matrix = event.target.closest('[data-inknfiber-size-matrix]');
        if (!matrix || !picker.contains(matrix)) return;
        const row = event.target.closest('[data-inknfiber-size-row]');
        if (!row) return;

        const input = row.querySelector('[data-inknfiber-size-quantity]');
        if (!input) return;

        const sizeButton = event.target.closest('[data-inknfiber-size-select]');
        const plusButton = event.target.closest('[data-inknfiber-size-plus]');
        const minusButton = event.target.closest('[data-inknfiber-size-minus]');
        if (sizeButton) {
          matrix.dataset.inknfiberActiveSize = row.dataset.size || '';
          updateMatrix(picker);
          return;
        }
        if (!plusButton && !minusButton) return;

        event.preventDefault();
        event.stopPropagation();

        const direction = plusButton ? 1 : -1;
        updateQuantityInput(picker, input, normalizeQuantityValue(input.value, quantityMaxForMatrix(matrix)) + direction);
      });

      matrix.addEventListener('input', (event) => {
        if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-inknfiber-size-quantity]')) return;

        event.stopPropagation();
        event.stopImmediatePropagation();
        updateQuantityInput(picker, event.target, event.target.value);
      });

      matrix.addEventListener('change', (event) => {
        if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-inknfiber-size-quantity]')) return;

        event.stopPropagation();
        event.stopImmediatePropagation();
        updateQuantityInput(picker, event.target, event.target.value);
      });

      picker.addEventListener('input', (event) => {
        if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-inknfiber-size-quantity]')) return;
        updateQuantityInput(picker, event.target, event.target.value);
      });

      picker.addEventListener('change', (event) => {
        if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-inknfiber-size-quantity]')) return;
        updateQuantityInput(picker, event.target, event.target.value);
      });
    }

    picker.addEventListener('change', () => {
      captureMatrixQuantities(picker);
      window.setTimeout(() => updateStockStatus(picker), 0);
    });
    picker.addEventListener('variant:update', () => {
      requestAnimationFrame(() => updateStockStatus(picker));
    });
    updateStockStatus(picker);
  }

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const context = matrixContextFromElement(form);

    if (!context) return;
    addMatrixToCart(event, context.picker, context.matrix, context.form);
  }, true);

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const addButton = event.target.closest('[data-inknfiber-matrix-add-to-cart], product-form-component [name="add"]');
    if (addButton) {
      const context = matrixContextFromElement(addButton);

      if (context) {
        addMatrixToCart(event, context.picker, context.matrix, context.form);
        return;
      }
    }

    const acceleratedCheckout = event.target.closest('[ref="acceleratedCheckoutButtonContainer"]');
    if (!acceleratedCheckout) return;

    const context = matrixContextFromElement(acceleratedCheckout);

    if (!context) return;
    buyMatrixNow(event, context.picker, context.matrix, context.form, acceleratedCheckout);
  }, true);

  function init() {
    updateB2BQuantityTiersVisibility();
    document.querySelectorAll('variant-picker').forEach(initPicker);
  }

  window.InkNfiberProductSizeMatrix = window.InkNfiberProductSizeMatrix || {};
  window.InkNfiberProductSizeMatrix.init = init;
  window.InkNfiberProductSizeMatrix.refreshPicker = updateStockStatus;
  window.InkNfiberProductSizeMatrix.setQuantity = updateQuantityInput;
  document.addEventListener('inknfiber:quick-add-content-updated', init);
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('inknfiber:b2b-cart-state', () => {
    document.querySelectorAll('[data-inknfiber-size-matrix][data-inknfiber-b2b-quantity-eligible="true"]')
      .forEach((matrix) => {
        const picker = matrix.closest('variant-picker');
        if (picker) updateStockStatus(picker);
      });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
